import React, { useEffect, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';

import AppBar from './modules/navigation/components/AdminNavigation';
import Sidebar, { DRAWER_WIDTH } from './modules/navigation/components/Sidebar';
import Routes from './routes';
import ContextsProvider from './contexts';
import ErrorBoundary from './shared/components/ErrorBoundary';
import AminosChat from './shared/components/AminosChat';
import ConsentBanner from './shared/components/ConsentBanner';
import GoogleTagManager from './shared/analytics/GoogleTagManager';
import theme from './theme';
import { useAuth } from './modules/auth/contexts/AuthenticationContext';
import { useImpersonation } from './modules/auth/contexts/ImpersonationContext';
import { useNeedsProfileCompletion, useProfile } from './modules/auth/hooks';
import { trackSignUp } from './shared/analytics/events';
import { syncFirstTouch } from './shared/analytics/attribution';
import ImpersonationBanner from './modules/auth/components/ImpersonationBanner';
import ProfileCompletionBanner from './modules/auth/components/ProfileCompletionBanner';
import ProfileDetailsDialog from './modules/auth/components/ProfileDetailsDialog';
import InviteClaimBanner from './modules/organization/components/InviteClaimBanner';
import VerifyEmailScreen from './modules/auth/components/VerifyEmailScreen';

const BANNER_HEIGHT = 40;

// Routes that should render full-bleed without the authenticated sidebar +
// AppBar chrome — even when the visitor is signed in. Used for marketing-
// adjacent surfaces (event checkout landing) that own their own header.
//
// Matching is a regex over location.pathname; keep this list short and
// explicit so we don't accidentally hide chrome on a Real page.
const CHROMELESS_ROUTES: RegExp[] = [
  /^\/events\/\d+\/checkout\/?$/,
  /^\/dominos26\/?$/,
];

// Pages that already ARE the profile-completion flow (or contain the full
// profile form) — nagging with the banner on top of them is just noise.
const PROFILE_BANNER_EXCLUDED_ROUTES: RegExp[] = [
  /^\/profile\/?$/,
  /^\/complete-profile\/?$/,
];

function AppLayout() {
  const { isAuthenticated, isLoading, needsEmailVerification } = useAuth();
  const { target: impersonationTarget } = useImpersonation();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const needsProfileCompletion = useNeedsProfileCompletion();
  const { data: profile } = useProfile();

  // GA4 `sign_up`. Lives here rather than in GoogleTagManager so shared/ doesn't
  // import from modules/. Idempotent per user (created_at recency + localStorage
  // guard), so re-running on every profile refetch is safe.
  useEffect(() => {
    if (profile?.id && profile.created_at) trackSignUp(profile.id, profile.created_at);
  }, [profile?.id, profile?.created_at]);

  // Hand the visitor's first touch to the API once they're somebody. This is the
  // earliest we can: Users are created server-side from the JWT inside the auth
  // filter, so there is no sign-up request body to carry the campaign on.
  // Server-side it's first-touch-wins and idempotent, so a repeat is harmless.
  useEffect(() => {
    if (profile?.id) void syncFirstTouch();
  }, [profile?.id]);

  // Email not verified: gate the whole app to a dedicated screen so the
  // dashboard's queries (which would all 403) never mount and spin.
  if (isAuthenticated && !isLoading && needsEmailVerification) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <VerifyEmailScreen />
        <ConsentBanner />
      </ThemeProvider>
    );
  }

  const isChromelessRoute = CHROMELESS_ROUTES.some((re) => re.test(location.pathname));
  const showChrome = isAuthenticated && !isLoading && !isChromelessRoute;
  const showBanner = showChrome && !!impersonationTarget;
  const showProfileBanner = showChrome
    && needsProfileCompletion
    && !PROFILE_BANNER_EXCLUDED_ROUTES.some((re) => re.test(location.pathname));
  // Banners stack: impersonation (loud, admin-only) on top, profile-completion
  // below it. Each contributes its height to the layout offset when visible.
  const topOffset = (showBanner ? BANNER_HEIGHT : 0) + (showProfileBanner ? BANNER_HEIGHT : 0);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ImpersonationBanner />
      {/* Must stay mounted unconditionally — see InviteClaimBanner for why. */}
      <InviteClaimBanner />
      {showProfileBanner && <ProfileCompletionBanner top={showBanner ? BANNER_HEIGHT : 0} />}
      <ProfileDetailsDialog />
      {/* Deliberately NOT gated on showChrome: CHROMELESS_ROUTES contains the
          public /events/:id/checkout landing, which is exactly where consent
          matters most. Bottom-fixed, so it stays out of the topOffset stack. */}
      <ConsentBanner />
      <Box sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        pt: `${topOffset}px`,
      }}>
        {showChrome && (
          <Sidebar
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
          />
        )}
        <Box
          id="page-content"
          component="main"
          sx={{
            // `position: relative` (without an explicit z-index) keeps this an
            // anchor for any absolute-positioned descendants without creating a
            // new stacking context. The previous `zIndex: 1` boxed every
            // descendant — including page-level Snackbars (z-1400) — into a
            // stacking context of z-1 at the root, where the Sidebar drawer
            // (z-1200 at the root) ended up painting on top. Result: toasts
            // hidden behind the sidebar. Dropping the z-index lets each
            // descendant's z-index compete in the root context as MUI assumes.
            position: 'relative',
            flexGrow: 1,
            minWidth: 0,
            width: showChrome ? { md: `calc(100% - ${DRAWER_WIDTH}px)` } : '100%',
            pt: showChrome ? '88px' : 0,
            px: showChrome ? { xs: 1.5, sm: 2.5, lg: 3 } : 0,
            pb: showChrome ? { xs: 2, sm: 3 } : 0,
            overflowX: 'hidden',
          }}
        >
          {showChrome && <AppBar onMenuClick={() => setMobileNavOpen(true)} bannerOffset={topOffset} />}
          <Box
            sx={{
              width: '100%',
              maxWidth: showChrome ? { xs: '100%', xl: 1440 } : 'none',
              mx: 'auto',
              minWidth: 0,
            }}
          >
            <Routes />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ContextsProvider>
          <AppLayout />
          <AminosChat />
          <GoogleTagManager />
        </ContextsProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
